// Print-time layer renderer — same layer vocabulary and visual conventions
// as ../report-card-designer/canvas/LayerPreview.tsx, but bound to REAL
// per-student data (from getSectionReportCards) instead of the designer's
// SAMPLE_GRADES placeholder. Kept as a separate component (not a LayerPreview
// prop) so the designer's canvas-editing code path can't regress from this.
import type { ReactNode, CSSProperties } from "react"
import type { Layer } from "../report-card-designer/canvas/types"
import type { SectionCardStudent, SectionReportCards, RcCourse } from "../../../service/report-card"

type Ctx = {
  school: SectionReportCards["school"]
  breakdown: SectionReportCards["breakdown"]
  klass: SectionReportCards["class"]
  section: SectionReportCards["section"]
  tpl: { columns?: Record<string, boolean>; signatures?: string[]; accentColor?: string }
  assetUrls: Record<string, string>
  onNeedUrl?: (key: string) => void
  gradeLegend: SectionReportCards["gradeLegend"]
}

// ── Inline markup renderer ───────────────────────────────────────────────
// Converts **bold**, *italic*, __underline__, ~~strike~~ markers to React
// elements. Called AFTER token interpolation so tokens like __{{name}}__
// resolve first, then the underline markup wraps the resolved value.
const renderMarkup = (text: string): ReactNode => {
  if (!text.includes("*") && !text.includes("_") && !text.includes("~")) return text
  const parts: ReactNode[] = []
  const regex = /\*\*([\s\S]+?)\*\*|__([\s\S]+?)__|~~([\s\S]+?)~~|\*([\s\S]+?)\*/g
  let last = 0; let k = 0; let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    if (match[1] !== undefined) parts.push(<strong key={k++}>{match[1]}</strong>)
    else if (match[2] !== undefined) parts.push(<u key={k++}>{match[2]}</u>)
    else if (match[3] !== undefined) parts.push(<s key={k++}>{match[3]}</s>)
    else if (match[4] !== undefined) parts.push(<em key={k++}>{match[4]}</em>)
    last = match.index + match[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : <>{parts}</>
}

// ── Token interpolation ─────────────────────────────────────────────────
// Builds the SAME nested scope DesignerCard.jsx's real templates address
// ({{student.full_name}}, {{campus.address}}, {{grade_scale.key_with_label}},
// etc.) so free-text "text" layers and "dynamic-text" layers both resolve
// against real data instead of printing the raw token.
const buildScope = (student: SectionCardStudent, ctx: Ctx) => {
  const pairs = ctx.gradeLegend
    .map(g => (g.remarks ? `${g.grade} : ${g.remarks}` : g.grade))
  const pctPairs = ctx.gradeLegend
    .map(g => g.minPercentage != null ? `${g.grade} ≥ ${g.minPercentage}%` : g.grade)
  const gradeScale = {
    key: pairs.join(" | "),
    key_with_label: pairs.length ? `Grading Key | ${pairs.join(" | ")}` : "",
    key_multiline: pairs.join("\n"),
    key_pct_only: pctPairs.join(" | "),
    name: "",
  }
  return {
    student: {
      full_name: student.studentName,
      gr_number: student.grNo,
      gr_no: student.grNo,
      roll_number: student.rollNo,
      roll_no: student.rollNo,
      result: student.summary.overallPassFail || "",
      promotion_status: student.summary.promotionStatus || "",
    },
    class: { name: ctx.klass.name },
    section: { name: ctx.section.name },
    school: { name: ctx.school.name, code: ctx.school.code, address: ctx.school.address, phone: ctx.school.phone },
    // RMS templates address the campus under "campus.*" — alias onto the same data.
    campus: { name: ctx.school.name, address: ctx.school.address, phone: ctx.school.phone },
    breakdown: { title: ctx.breakdown.title, term: ctx.breakdown.term || "", session_name: ctx.breakdown.session || "" },
    attendance: { days_present: student.attendance.daysPresent, days_total: student.attendance.daysTotal },
    summary: {
      overall_grade: student.summary.overallGrade || "",
      overall_percentage: student.summary.overallPercentage,
      position: student.summary.positionWithSuffix,
      result: student.summary.overallPassFail || "",
      promotion_status: student.summary.promotionStatus || "",
    },
    grade_scale: gradeScale,
  }
}

const TOKEN_FILTERS: Record<string, (s: string) => string> = {
  upper: s => s.toUpperCase(),
  lower: s => s.toLowerCase(),
  title: s => s.replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase()),
  sentence: s => (s.length ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s),
}

// Resolve every `{{path.to.value | filter}}` in `text` against the nested
// `scope` object. Unknown paths resolve to "" (silent) so a misconfigured
// template doesn't crash the page — same behaviour as the server renderer.
const interpolate = (text: string, scope: Record<string, any>): string => {
  if (typeof text !== "string") return ""
  return text.replace(/\{\{([^}]+)\}\}/g, (_, raw) => {
    const segments = String(raw).split("|").map((s: string) => s.trim()).filter(Boolean)
    if (!segments.length) return ""
    const [pathRaw, ...filterNames] = segments
    let cur: any = scope
    for (const seg of pathRaw.split(".")) {
      if (cur == null) return ""
      cur = cur[seg]
    }
    if (cur == null || typeof cur === "object") return ""
    let out = String(cur)
    for (const f of filterNames) out = (TOKEN_FILTERS[f.toLowerCase()] || (v => v))(out)
    return out
  })
}

// Resolve a `layer.field` path (used by "dynamic-text") against the same
// nested scope `interpolate` uses, so both token styles share one source.
const resolveField = (field: string | undefined, scope: Record<string, any>): string => {
  if (!field) return ""
  let cur: any = scope
  for (const seg of field.split(".")) {
    if (cur == null) return ""
    cur = cur[seg]
  }
  return cur == null || typeof cur === "object" ? "" : String(cur)
}

// ── Grade palette ────────────────────────────────────────────────────────
// "NA" (not assessed) is intentionally NOT a fail color — it means
// "ungraded yet", not "failed". RMS renders it as a neutral grey medal,
// distinct from genuine fail grades (F/U/AB).
const FAIL_GRADES = new Set(["F", "U", "AB"])
const NA_PALETTE = { outer: "#9ca3af", inner: "#e5e7eb", letterColor: "#374151", stroke: "#4b5563" }
const FAIL_PALETTE = { outer: "#dc2626", inner: "#fecaca", letterColor: "#fff1f2", stroke: "#7f1d1d" }
const GRADE_PALETTE: Record<string, { outer: string; inner: string; letterColor: string; stroke: string }> = {
  "A*": { outer: "#3d5ee1", inner: "#dfe6ff", letterColor: "#eef2ff", stroke: "#2e3192" },
  "A+": { outer: "#3d5ee1", inner: "#dfe6ff", letterColor: "#eef2ff", stroke: "#2e3192" },
  A: { outer: "#10b981", inner: "#a7f3d0", letterColor: "#ecfdf5", stroke: "#064e3b" },
  B: { outer: "#0ea5e9", inner: "#bae6fd", letterColor: "#eef2ff", stroke: "#1e3a8a" },
  C: { outer: "#3d5ee1", inner: "#dfe6ff", letterColor: "#eef2ff", stroke: "#2e3192" },
  D: { outer: "#4f6add", inner: "#eef2ff", letterColor: "#eef2ff", stroke: "#2e3192" },
  E: { outer: "#3d5ee1", inner: "#dfe6ff", letterColor: "#eef2ff", stroke: "#7f1d1d" },
}

const paletteForGrade = (letter: string, colorCode: string | null) => {
  const L = (letter || "").toUpperCase()
  if (L === "NA") return NA_PALETTE
  if (FAIL_GRADES.has(L)) return FAIL_PALETTE
  if (colorCode && /^#[0-9a-f]{6}$/i.test(colorCode)) {
    const n = parseInt(colorCode.slice(1), 16)
    const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff
    const mix = (t: number) => `#${[r, g, b].map(c => Math.round(c + (255 - c) * t).toString(16).padStart(2, "0")).join("")}`
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
    return { outer: colorCode, inner: mix(0.55), letterColor: lum < 0.45 ? "#ffffff" : "#0f172a", stroke: colorCode }
  }
  return GRADE_PALETTE[L] || GRADE_PALETTE.A
}

const GradeBadge = ({ letter, colorCode }: { letter: string; colorCode: string | null }) => {
  const { outer, inner, letterColor, stroke } = paletteForGrade(letter, colorCode)
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: "88%", aspectRatio: "1/1", borderRadius: "50%", background: `radial-gradient(circle at 30% 25%, ${inner} 0%, ${outer} 75%)`, border: `2px solid ${outer}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: "'Fredoka One','Comic Sans MS','Trebuchet MS',system-ui,sans-serif", fontWeight: 900, fontSize: "55%", lineHeight: 0.9, color: letterColor, WebkitTextStroke: `1px ${stroke}` }}>
          {letter || "—"}
        </span>
      </div>
    </div>
  )
}

const cleanSubjectName = (name?: string) =>
  String(name || "").replace(/^subject\s*\[\d+\]\s*/i, "").replace(/\s*\(\d+sc\)\s*$/i, "").trim()

const findCourse = (student: SectionCardStudent, name?: string, subjectIndex?: number): RcCourse | (SectionCardStudent["missingCourses"][number] & { obtainedMarks?: null }) | null => {
  const all = [...student.courses, ...student.missingCourses]
  const target = cleanSubjectName(name).toLowerCase()
  if (target) {
    const norm = (s: string) => (s || "").toLowerCase().trim()
    const exact = all.find(c => norm(c.courseName) === target)
    if (exact) return exact
    const prefix = all.find(c => {
      const n = norm(c.courseName)
      const [shorter, longer] = n.length <= target.length ? [n, target] : [target, n]
      return shorter.length >= 4 && longer.startsWith(shorter)
    })
    if (prefix) return prefix
  }
  if (Number.isFinite(subjectIndex) && all[subjectIndex as number]) return all[subjectIndex as number]
  return null
}

const fmt = (v: number | null | undefined, dp = 2) => {
  if (v == null) return "—"
  const n = Number(v)
  if (Number.isNaN(n)) return "—"
  return n % 1 === 0 ? String(n) : n.toFixed(dp).replace(/\.?0+$/, "")
}

export const renderPrintLayer = (layer: Layer, scale: number, student: SectionCardStudent, ctx: Ctx): ReactNode => {
  const s = (v: number) => v * scale
  const rp = (layer._rmsProps || {}) as Record<string, any>
  const col = ctx.tpl.columns || {}
  const allOff = !col.showObtained && !col.showTotal && !col.showPercent && !col.showGrade && !col.showRemarks
  const S = {
    obt: allOff || col.showObtained !== false,
    tot: allOff || col.showTotal !== false,
    pct: allOff || col.showPercent !== false,
    grd: allOff || col.showGrade !== false,
    res: allOff || col.showRemarks !== false,
  }

  switch (layer.type) {
    case "image": {
      const url = layer.src ? ctx.assetUrls[layer.src] : null
      if (layer.src && !url && ctx.onNeedUrl) ctx.onNeedUrl(layer.src)
      return url
        ? <img src={url} style={{ width: "100%", height: "100%", objectFit: layer.fit || "contain", display: "block" }} alt="" />
        : null
    }

    case "text": {
      const scope = buildScope(student, ctx)
      const value = interpolate(layer.content || "", scope)
      return (
        <div style={{
          width: "100%", height: "100%", fontFamily: layer.fontFamily || "inherit", fontSize: s(layer.fontSize || 14),
          fontWeight: layer.fontWeight || "normal", fontStyle: layer.fontStyle || "normal", color: layer.color || "#0f172a",
          textAlign: (layer.textAlign as any) || "left", lineHeight: layer.lineHeight || 1.2, overflow: "hidden", padding: s(4), opacity: layer.opacity ?? 1,
          whiteSpace: "pre-line",
        }}>
          {renderMarkup(value)}
        </div>
      )
    }

    case "dynamic-text": {
      const scope = buildScope(student, ctx)
      const value = resolveField(layer.field, scope)
      return (
        <div style={{ width: "100%", height: "100%", fontFamily: layer.fontFamily || "inherit", fontSize: s(layer.fontSize || 14), fontWeight: layer.fontWeight || "normal", color: layer.color || "#0f172a", textAlign: (layer.textAlign as any) || "left", lineHeight: layer.lineHeight || 1.2, overflow: "hidden", padding: s(4), opacity: layer.opacity ?? 1 }}>
          {layer.prefix || ""}{value}{layer.suffix || ""}
        </div>
      )
    }

    case "shape":
      return (
        <div style={{ width: "100%", height: "100%", background: layer.fill || "transparent", border: `${s(layer.strokeWidth || 1)}px solid ${layer.stroke || "transparent"}`, borderRadius: layer.shape === "ellipse" ? "50%" : s(layer.borderRadius || 0), opacity: layer.opacity ?? 1 }} />
      )

    case "line":
      return (
        <div style={{ width: "100%", height: s(layer.thickness || 2), background: layer.color || "#0f172a", borderRadius: layer.rounded ? s(layer.thickness || 2) / 2 : 0, position: "absolute", top: "50%", transform: "translateY(-50%)" }} />
      )

    case "header":
      return (
        <div style={{ width: "100%", height: "100%", background: layer.headerColor || "#0f172a", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: `${s(8)}px ${s(16)}px`, boxSizing: "border-box" }}>
          <div style={{ fontWeight: 800, fontSize: s(18), letterSpacing: s(1.5), textTransform: "uppercase" }}>{ctx.school.name || "School"}</div>
          <div style={{ fontSize: s(10), marginTop: s(4), opacity: 0.85 }}>{ctx.breakdown.title}</div>
        </div>
      )

    case "student_info":
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", gap: s(8), padding: s(6), boxSizing: "border-box", background: "#f9fafb" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: s(13), color: "#111827", marginBottom: s(2) }}>{student.studentName}</div>
            <div style={{ fontSize: s(9), color: "#374151", lineHeight: 1.6 }}>
              <div>GR No: {student.grNo} &nbsp;|&nbsp; Roll No: {student.rollNo || "—"}</div>
              <div>Class: {ctx.klass.name} &nbsp;|&nbsp; Section: {ctx.section.name}</div>
            </div>
          </div>
          {layer.showPhoto && (
            <div style={{ width: s(56), height: s(70), background: "#e5e7eb", border: `${s(1)}px solid #d1d5db`, borderRadius: s(4), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
              {student.photo && ctx.assetUrls[student.photo]
                ? <img src={ctx.assetUrls[student.photo]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <i className="ti ti-user" style={{ fontSize: s(24), color: "#9ca3af" }} />}
            </div>
          )}
        </div>
      )

    case "subject_table": {
      const allCourses = [...student.courses, ...student.missingCourses]
      const thBg = layer.headerColor || "#0f172a"
      const fs = s(8.5)
      const pad = `${s(3)}px ${s(5)}px`
      return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs, tableLayout: "fixed" }}>
            <thead>
              <tr style={{ background: thBg, color: "#fff" }}>
                <th style={{ padding: pad, textAlign: "left", fontWeight: 700, width: "36%" }}>Subject</th>
                {S.obt && <th style={{ padding: pad, width: "20%" }}>Marks</th>}
                {S.grd && <th style={{ padding: pad, width: "13%" }}>Grade</th>}
              </tr>
            </thead>
            <tbody>
              {allCourses.map((c, i) => {
                const isMissing = !("obtainedMarks" in c)
                return (
                  <tr key={c.courseId} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                    <td style={{ padding: pad, borderBottom: `${s(0.5)}px solid #e5e7eb`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.courseName}</td>
                    {S.obt && <td style={{ padding: pad, textAlign: "center", borderBottom: `${s(0.5)}px solid #e5e7eb` }}>{isMissing ? "NA" : `${fmt((c as RcCourse).obtainedMarks)}/${fmt(c.subCategories.reduce((a, x) => a + x.marks, 0))}`}</td>}
                    {S.grd && <td style={{ padding: pad, textAlign: "center", borderBottom: `${s(0.5)}px solid #e5e7eb`, color: ctx.tpl.accentColor || "#1a56db", fontWeight: 700 }}>{isMissing ? "NA" : (c as RcCourse).grade || "—"}</td>}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    }

    case "grade_key": {
      const rows = ctx.gradeLegend.length
        ? ctx.gradeLegend.map(g => [g.grade, `≥${g.minPercentage}%`])
        : [["A+", "90-100"], ["A", "80-89"], ["B", "70-79"], ["C", "60-69"], ["D", "50-59"], ["F", "Below 50"]]
      return (
        <div style={{ width: "100%", height: "100%", padding: s(6), boxSizing: "border-box", border: `${s(1)}px solid #e5e7eb`, borderRadius: s(2) }}>
          <div style={{ fontSize: s(8), fontWeight: 700, color: "#374151", marginBottom: s(4), textTransform: "uppercase" }}>Grade Key</div>
          <div style={{ display: "flex", gap: s(8), flexWrap: "wrap" }}>
            {rows.map(([g, r]) => (
              <div key={g} style={{ fontSize: s(8) }}>
                <span style={{ fontWeight: 700, color: ctx.tpl.accentColor || "#1a56db" }}>{g}</span>
                <span style={{ color: "#6b7280", marginLeft: s(2) }}>{r}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }

    case "signatures": {
      const sigs = ctx.tpl.signatures?.length ? ctx.tpl.signatures : ["Class Teacher", "Principal", "Date"]
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "flex-end", borderTop: `${s(1)}px solid #e5e7eb`, paddingTop: s(4), boxSizing: "border-box" }}>
          {sigs.map((sig, i) => (
            <div key={i} style={{ flex: 1, textAlign: "center" }}>
              <div style={{ borderTop: `${s(1)}px solid #0f172a`, marginInline: s(12), paddingTop: s(3), fontSize: s(9), color: "#374151" }}>{sig}</div>
            </div>
          ))}
        </div>
      )
    }

    case "attendance": {
      const present = student.attendance.daysPresent, total = student.attendance.daysTotal
      const ratio = total > 0 ? Math.min(1, present / total) : 0
      return (
        <div style={{ width: "100%", height: "100%", padding: s(6), boxSizing: "border-box", background: "#f0fdf4", border: `${s(1)}px solid #bbf7d0`, borderRadius: s(3) }}>
          <div style={{ fontSize: s(9), fontWeight: 600, color: "#15803d", marginBottom: s(3) }}>Attendance: {present} / {total} days</div>
          <div style={{ height: s(7), background: "#dcfce7", borderRadius: s(4), overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${ratio * 100}%`, background: "#16a34a", borderRadius: s(4) }} />
          </div>
        </div>
      )
    }

    case "rms_campus_header": {
      const key = ctx.school.headerImageKey
      const url = key ? ctx.assetUrls[key] : null
      if (key && !url && ctx.onNeedUrl) ctx.onNeedUrl(key)
      if (url) return <img src={url} style={{ width: "100%", height: "100%", objectFit: "contain" }} alt="" />
      const bg = rp.gradient || (rp.bg && rp.bg !== "transparent" ? rp.bg : "#b59350")
      return (
        <div style={{ width: "100%", height: "100%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: s(8), boxSizing: "border-box", overflow: "hidden" }}>
          <div style={{ fontSize: s(rp.fontSize || 18), fontWeight: rp.fontWeight || 800, color: rp.color || "#fff", textAlign: "center", lineHeight: 1.3, textTransform: rp.textUpper ? "uppercase" : "none" }}>
            {ctx.school.headerText || ctx.school.name}
          </div>
        </div>
      )
    }

    case "rms_campus_logo": {
      const logoKey = ctx.school.logoKey
      const logoUrl = logoKey ? ctx.assetUrls[logoKey] : null
      if (logoKey && !logoUrl && ctx.onNeedUrl) ctx.onNeedUrl(logoKey)
      return (
        <div style={{ width: "100%", height: "100%", background: logoUrl ? "transparent" : (rp.bg && rp.bg !== "transparent" ? rp.bg : "#f1f5f9"), border: `${s(1)}px solid #d1d5db`, borderRadius: (rp.borderRadius ?? 1) >= 0.9 ? "50%" : s((rp.borderRadius ?? 1) * 10), display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          {logoUrl
            ? <img src={logoUrl} style={{ width: "100%", height: "100%", objectFit: "contain" }} alt="" />
            : <i className="ti ti-building-school" style={{ fontSize: s(32), color: "#94a3b8" }} />}
        </div>
      )
    }

    case "rms_photo":
      return (
        <div style={{ width: "100%", height: "100%", background: rp.bg || "#f5f5f4", border: `${s((rp.borderWidth ?? 0.4) * 10)}px solid ${rp.borderColor || "#2e3192"}`, borderRadius: s((rp.borderRadius ?? 2) * 4), display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          {student.photo && ctx.assetUrls[student.photo]
            ? <img src={ctx.assetUrls[student.photo]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <i className="ti ti-user-circle" style={{ fontSize: s(40), color: "#9ca3af" }} />}
        </div>
      )

    case "rms_grade_badge": {
      const course = findCourse(student, rp.name, rp.subjectIndex)
      const letter = course
        ? (("status" in course && course.status === "Not Assessed") ? "NA" : ("status" in course && course.status === "Absent") ? "AB" : (course as RcCourse).grade || "—")
        : student.summary.overallGrade || "—"
      const colorCode = course ? (course as RcCourse).colorCode ?? null : null
      return <GradeBadge letter={letter || "—"} colorCode={colorCode} />
    }

    case "rms_subject_table": {
      const course = findCourse(student, rp.name, rp.subjectIndex) || student.courses[0]
      if (!course) return null
      const isMissing = !("obtainedMarks" in course)
      const cells = course.subCategories
      const showHeader = rp.showHeader !== false
      const showSubcats = rp.showSubcats !== false
      const showTotal = rp.showTotal !== false
      const realCells = isMissing ? [] : (course as RcCourse).cells
      const max = cells.reduce((a, c) => a + c.marks, 0)

      // ── "styled" variant (Grade I/II/III/IV/V/VI templates) ──────────
      // Mirrors SubjectBlockStyledBody.jsx exactly: pill header + progress-
      // bar rows + accentDark total bar with circular grade badge.
      if (rp.variant === "styled") {
        const accent = rp.accentColor || "#0d6e6e"
        const accentDark = rp.accentDarkColor || "#054b4b"
        const baseFs = rp.fontSize || 9
        const showProgress = rp.progressBars !== false
        const noSubcats = cells.length <= 1
        const totalRightPad = noSubcats ? "20mm" : "17mm"
        const totalMinH = noSubcats ? "9mm" : "6mm"
        const obtainedTotal = isMissing ? "—" : fmt((course as RcCourse).obtainedMarks)
        const gradeLetter = isMissing ? "NA" : (course as RcCourse).grade || "NA"
        const gradeHex = paletteForGrade(gradeLetter, (course as RcCourse)?.colorCode ?? null).outer
        return (
          <div style={{ width: "100%", boxSizing: "border-box", background: rp.bodyBg || "#ffffff", borderRadius: "2mm", fontFamily: '"Trebuchet MS","Comic Sans MS",system-ui,sans-serif', fontSize: `${baseFs}pt`, display: "flex", flexDirection: "column", overflow: "visible", border: "0.2mm solid rgba(0,0,0,0.06)" }}>
            {showHeader && (
              <div style={{ background: accent, color: "#ffffff", borderRadius: "99mm", border: "0.4mm solid #ffffff", padding: "0.7mm 2mm", margin: "1mm", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 0.2mm 0.5mm rgba(0,0,0,0.18)" }}>
                <div style={{ textAlign: "center", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.25mm", fontSize: `${baseFs + 1}pt`, lineHeight: 1.1 }}>
                  {cleanSubjectName(course.courseName) || course.courseName}
                </div>
              </div>
            )}
            {showSubcats && cells.length > 0 && (
              <div style={{ padding: "0.6mm 1.5mm 0.4mm 1.5mm", color: rp.rowColor || "#1f2937", display: "flex", flexDirection: "column", gap: "0.8mm" }}>
                {cells.map((c, i) => {
                  const real = realCells[i]
                  const val = isMissing || !real ? "NA" : real.is_na ? "NA" : real.is_absent ? "AB" : fmt(real.value)
                  const valNum = Number(val); const maxNum = Number(c.marks)
                  const pct = Number.isFinite(maxNum) && Number.isFinite(valNum) && maxNum > 0 ? Math.max(0, Math.min(100, (valNum / maxNum) * 100)) : 0
                  return (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: showProgress ? "1fr 8mm 25% 8mm" : "1fr 8mm 8mm", alignItems: "center", gap: "1.2mm", padding: "0.1mm 0" }}>
                      <div style={{ fontStyle: "italic", fontWeight: 600, textTransform: "capitalize" }}>{c.description}</div>
                      <div style={{ fontFamily: "monospace", fontWeight: 800, textAlign: "right", color: rp.rowColor || "#1f2937" }}>{val}</div>
                      {showProgress && (
                        <div style={{ position: "relative", height: "2.4mm", borderRadius: "99mm", background: accentDark, overflow: "hidden", border: `0.2mm solid ${accent}` }}>
                          <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: `${pct}%`, background: "linear-gradient(90deg,rgba(255,255,255,0.85),rgba(255,255,255,0.55))", borderRadius: "99mm" }} />
                        </div>
                      )}
                      <div style={{ border: `0.25mm solid ${accent}`, borderRadius: "99mm", padding: "0.1mm 0.5mm", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: accent, background: "#ffffff" }}>
                        {String(c.marks)}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {showTotal && (
              <div style={{ display: "flex", justifyContent: "flex-start", padding: "0 1mm 1mm 1mm", flexShrink: 0 }}>
                <div style={{ width: "100%", position: "relative", background: accentDark, color: "#ffffff", padding: `0.9mm ${totalRightPad} 0.9mm 2.5mm`, display: "flex", alignItems: "center", fontWeight: 800, borderRadius: "1.5mm", minHeight: totalMinH, boxSizing: "border-box" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "1.6mm" }}>
                    <div style={{ fontStyle: "italic", fontWeight: 800, fontSize: `${baseFs + 1}pt`, letterSpacing: "0.1mm" }}>Total</div>
                    <div style={{ opacity: 0.55, fontSize: `${baseFs}pt`, lineHeight: 1 }}>·</div>
                    <div style={{ fontFamily: "monospace", fontWeight: 900, fontSize: `${baseFs + 1}pt` }}>{String(max)}</div>
                  </div>
                  <div style={{ position: "absolute", right: "-2mm", top: "50%", transform: "translateY(-50%)", width: "15mm", height: "15mm", borderRadius: "50%", background: `radial-gradient(circle at 30% 25%,rgba(255,255,255,0.65) 0%,${gradeHex} 55%,rgba(0,0,0,0.25) 100%)`, border: `0.4mm solid ${gradeHex}`, boxShadow: "inset 0 0.3mm 0.6mm rgba(255,255,255,0.55),0 0.5mm 0.9mm rgba(0,0,0,0.35)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#ffffff", textShadow: "0 0 0.4mm rgba(0,0,0,0.7)", padding: "0.4mm", boxSizing: "border-box" }}>
                    <div style={{ fontSize: "140%", fontWeight: 900, lineHeight: 1, fontFamily: 'Georgia,"Times New Roman",serif' }}>{obtainedTotal}</div>
                    <div style={{ fontSize: "75%", fontWeight: 900, lineHeight: 1, marginTop: "0.2mm", letterSpacing: "0.2mm" }}>{gradeLetter}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      }

      // ── pill cells variant (Playgroup / Montessori templates) ─────────
      const headerBg = rp.headerBg || rp.accentColor || "#004080"
      const headerColor = rp.headerColor || "#ffffff"
      const bodyBg = rp.bodyBg || "#ffffff"
      const borderColor = rp.borderColor || "#cbd5e1"
      const totalBg = rp.totalBg || "#ffffff"
      const totalColor = rp.totalColor || headerBg
      const pillCells = !!rp.pillCells
      const total = isMissing ? "NA" : fmt((course as RcCourse).obtainedMarks)
      const pillStyle: CSSProperties = {
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: s(14), padding: `${s(0.3)}px ${s(1)}px`, borderRadius: s(3),
        border: `${s(0.18)}px solid ${rp.pillBorderColor || "#7dd3fc"}`,
        background: rp.pillBg || "#ffffff", color: rp.pillColor || rp.rowColor || "#1f2937",
        fontFamily: "monospace", fontWeight: 800, fontSize: "90%",
      }
      return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden", border: `${s(0.5)}px solid ${borderColor}`, borderRadius: s(3), background: bodyBg, display: "flex", flexDirection: "column", fontSize: s(rp.fontSize || 8) }}>
          {showHeader && <div style={{ background: headerBg, color: headerColor, padding: `${s(3)}px ${s(5)}px`, fontWeight: 700, flexShrink: 0 }}>{cleanSubjectName(course.courseName) || course.courseName}</div>}
          {showSubcats && (
            <div style={{ padding: `${s(0.4)}px ${s(1)}px`, color: rp.rowColor || "#1f2937" }}>
              {cells.map((c, i) => {
                const real = realCells[i]
                const valDisplay = isMissing || !real ? "NA" : real.is_na ? "NA" : real.is_absent ? "AB" : fmt(real.value)
                return pillCells ? (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: `1fr ${s(14)}px ${s(14)}px`, alignItems: "center", justifyItems: "center", gap: s(1), padding: `${s(0.5)}px 0` }}>
                    <div style={{ fontWeight: 600, justifySelf: "start", textTransform: "capitalize" }}>{c.description}</div>
                    <span style={{ ...pillStyle, width: "100%" }}>{c.marks}</span>
                    <span style={{ ...pillStyle, width: "100%" }}>{valDisplay}</span>
                  </div>
                ) : (
                  <div key={i} style={{ display: "flex", alignItems: "center", padding: `${s(2)}px ${s(4)}px`, borderBottom: `${s(0.5)}px solid ${borderColor}` }}>
                    <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.description}</div>
                    <div style={{ width: s(50), textAlign: "right", fontWeight: 600 }}>{valDisplay}/{c.marks}</div>
                  </div>
                )
              })}
            </div>
          )}
          {showTotal && (
            <div style={{ display: "flex", alignItems: "center", gap: s(1.5), padding: `${s(2.5)}px ${s(4)}px`, background: pillCells ? "transparent" : totalBg, fontWeight: 700, borderTop: `${s(0.5)}px solid ${borderColor}`, color: totalColor, flexShrink: 0 }}>
              <span>Total</span>
              {pillCells
                ? <span style={{ ...pillStyle, padding: `${s(0.5)}px ${s(3)}px`, fontWeight: 900 }}>{total}/{max}</span>
                : <span style={{ fontFamily: "monospace" }}>{total}/{max}</span>}
            </div>
          )}
        </div>
      )
    }

    case "rms_attendance_bar": {
      const present = student.attendance.daysPresent, total = student.attendance.daysTotal
      const ratio = total > 0 ? Math.min(1, present / total) : 0
      return (
        <div style={{ width: "100%", height: "100%", padding: `${s(3)}px ${s(5)}px`, boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "center", gap: s(3), border: `${s(0.4)}px solid ${rp.borderColor || "#2e3192"}`, borderRadius: s((rp.borderRadius ?? 1.5) * 4) }}>
          {rp.showLabel !== false && <div style={{ fontSize: s(7.5), fontWeight: 700, color: rp.labelColor || "#2e3192" }}>{rp.labelText || "Attendance"}</div>}
          <div style={{ height: s(8), background: rp.trackColor || "#e5e7eb", borderRadius: s(4), overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${ratio * 100}%`, background: rp.barColor || "#4ade80", borderRadius: s(4) }} />
          </div>
          {rp.showRatio !== false && <div style={{ fontSize: s(7), color: rp.labelColor || "#2e3192", textAlign: "center" }}>{present} / {total} Days</div>}
        </div>
      )
    }

    case "rms_congrats": {
      const grade = (student.summary.overallGrade || "").toUpperCase()
      const firstName = student.studentName.split(/\s+/)[0] || ""
      const isFail = student.summary.overallPassFail === "Fail"
      const blurb = student.blurb || "Keep working hard!"
      const tail = isFail ? `Stay strong, ${firstName}.` : `Congratulations, ${firstName}!`
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: s(6), boxSizing: "border-box", textAlign: "center", fontSize: s(rp.fontSize || 14), fontWeight: rp.fontWeight || 800, fontStyle: rp.italic ? "italic" : "normal", color: rp.color || "#2e3192" }}>
          {blurb} {tail}
        </div>
      )
    }

    default:
      return null
  }
}

export default renderPrintLayer
